import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination, 
  Chip, 
  Button,
  Grid,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Autocomplete,
  TextField,
  Stack
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { itemApi } from '../../features/settings/items/api/itemApi';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';

type OrderStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

const PROCEDURE_STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

interface FinancialOrder {
  id: string;
  appointmentNumber: string;
  patientName: string;
  serviceName: string;
  amount: number;
  paidAmount: number;
  pendingAmount: number;
  orderDate: string;
  status: OrderStatus;
  invoiceNumber?: string;
  doctorId: string;
  doctorName?: string;
  appointment?: any;
  item?: any;
}

const ServiceReport: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Filters: fromDate, toDate, status, doctor
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const defaultFromDate = new Date(today);
  const defaultToDate = new Date();
  defaultToDate.setHours(23, 59, 59, 999);
  const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate);
  const [toDate, setToDate] = useState<Date | null>(defaultToDate);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [doctorFilter, setDoctorFilter] = useState<string>('all');

  // Doctors list
  const [doctors, setDoctors] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    import('../../features/employees/api/employeeApi').then(({ employeeApi }) => {
      employeeApi.getDoctors().then((data) => {
        setDoctors(data.map(doc => ({ id: doc.id, name: `${doc.firstName} ${doc.lastName}`.trim() }))); 
      });
    });
  }, []);

  // Items list for item filter
  const [items, setItems] = useState<{ id: string; name: string }[]>([]);
  const [selectedItem, setSelectedItem] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    itemApi.getItems({ page: 1, limit: 100 }).then((resp) => {
      setItems(resp.data.map((item: any) => ({ id: item.id, name: item.itemName })));
    });
  }, []);

  // Patients list for patient filter
  const [patients, setPatients] = useState<{ id: string; name: string }[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    import('../../features/patients/api/patientApi').then(({ patientApi }) => {
      patientApi.getPatients({ page: 1, limit: 100 }).then((resp) => {
        setPatients(resp.data.map((p: any) => ({ id: p.id, name: p.fullName }))); 
      });
    });
  }, []);

  // Orders
  const [orders, setOrders] = useState<FinancialOrder[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchOrders = () => {
    // Always send status in uppercase (except 'all')
    const statusParam = statusFilter !== 'all' ? statusFilter.toUpperCase() : undefined;
    if (!fromDate || !toDate) return;
    setLoading(true);
    import('../../api/ReportApi').then(({ ReportApi }) => {
      // Convert dates to Asia/Kolkata (IST) before sending
      const toIST = (date: Date) => {
        const utc = date.getTime() + (date.getTimezoneOffset() * 60000);
        const IST_OFFSET = 5.5 * 60 * 60 * 1000;
        return new Date(utc + IST_OFFSET);
      };
      // Format dates to avoid timezone issues
      const formatDateForAPI = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      ReportApi.getFinancialOrdersAdvanced({
        fromDate: fromDate ? formatDateForAPI(fromDate) : undefined,
        toDate: toDate ? formatDateForAPI(toDate) : undefined,
        status: statusParam,
        doctorId: doctorFilter !== 'all' ? doctorFilter : undefined,
        patientId: selectedPatient ? selectedPatient.id : undefined,
        itemId: selectedItem ? selectedItem.id : undefined,
      })
        .then((data: any[]) => {
          console.log('--- SERVICE REPORT RAW API RESPONSE ---');
          console.log('Data length:', data.length);
          console.log('Raw data:', data);
          
          const mappedOrders = data.map(order => {
            const mapped = {
              id: order.id || order.appointment?.appointmentNumber || '',
              patientName: order.appointment?.patient?.fullName || '',
              serviceName: order.item?.itemName || order.serviceName || '',
              amount: order.grandTotal ?? 0,
              paidAmount: order.paidAmount ?? 0,
              pendingAmount: order.pendingAmount ?? 0,
              orderDate: order.orderDate || order.createdAt || order.createdDate,
              status: (order.procedureStatus || '').toLowerCase() as OrderStatus,
              appointmentNumber: order.appointment?.appointmentNumber || order.appointmentNumber || order.orderNumber || order.id,
              doctorId: order.appointment?.doctor?.id || '',
              doctorName: [order.appointment?.doctor?.firstName, order.appointment?.doctor?.lastName].filter(Boolean).join(' '),
              appointment: order.appointment,
              item: order.item,
            };
            console.log('--- SERVICE REPORT MAPPING ORDER ---');
            console.log('Raw order ID:', order.id);
            console.log('Raw order item:', order.item);
            console.log('Selected item filter:', selectedItem);
            console.log('Item ID match:', order.item?.id === selectedItem?.id);
            console.log('Mapped order:', mapped);
            return mapped;
          });
          
          console.log('--- SERVICE REPORT MAPPED ORDERS ---');
          console.log('Mapped orders:', mappedOrders);
          
          setOrders(mappedOrders);
        })
        .finally(() => setLoading(false));
    });
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, []);

  console.log('--- SERVICE REPORT CURRENT FILTERS ---');
  console.log('Status Filter:', statusFilter);
  console.log('Doctor Filter:', doctorFilter);
  console.log('Selected Patient:', selectedPatient);
  console.log('Selected Item:', selectedItem);
  console.log('Total Orders Before Filtering:', orders.length);

  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter.toLowerCase();
    const matchesDoctor = doctorFilter === 'all' || order.doctorId === doctorFilter;
    const matchesPatient = !selectedPatient || order.patientName === selectedPatient.name;
    const matchesItem = !selectedItem || order.item?.id === selectedItem.id;
    
    console.log('--- SERVICE REPORT FILTERING ORDER ---');
    console.log('Order ID:', order.id);
    console.log('Order Patient Name:', order.patientName);
    console.log('Selected Patient Name:', selectedPatient?.name);
    console.log('Order Item ID:', order.item?.id);
    console.log('Selected Item ID:', selectedItem?.id);
    console.log('Order Status:', order.status, 'vs Filter:', statusFilter);
    console.log('Matches Status:', matchesStatus);
    console.log('Matches Doctor:', matchesDoctor);
    console.log('Matches Patient:', matchesPatient);
    console.log('Matches Item:', matchesItem);
    console.log('Final Match:', matchesStatus && matchesDoctor && matchesPatient && matchesItem);
    
    return matchesStatus && matchesDoctor && matchesPatient && matchesItem;
  });

  console.log('--- SERVICE REPORT FILTERING RESULTS ---');
  console.log('Filtered Orders Count:', filteredOrders.length);
  console.log('Filtered Orders:', filteredOrders);

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleDoctorFilterChange = (event: SelectChangeEvent) => {
    setDoctorFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Service Report
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          View all ordered services with their completion status
        </Typography>

      <Card sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={2}>
          {/* First Row: Dates, Status, Doctor */}
          <Grid item xs={12} md={3}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={(date) => setFromDate(date)}
              format="dd/MM/yyyy"
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={(date) => setToDate(date)}
              format="dd/MM/yyyy"
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                {PROCEDURE_STATUS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value.toLowerCase()}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Doctor</InputLabel>
              <Select
                value={doctorFilter}
                label="Doctor"
                onChange={handleDoctorFilterChange}
              >
                <MenuItem value="all">All Doctors</MenuItem>
                {doctors.map((doctor) => (
                  <MenuItem key={doctor.id} value={doctor.id}>
                    {doctor.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
    
          {/* Second Row: Item, Patient, Buttons (right-aligned) */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={items}
              value={selectedItem}
              onChange={(_, value) => setSelectedItem(value)}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Item" size="small" fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={patients}
              value={selectedPatient}
              onChange={(_, value) => setSelectedPatient(value)}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Patient" size="small" fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center">
            <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
    variant="outlined"
    color="primary"
    startIcon={<DownloadIcon />}
    onClick={() => {
    // Utility: Convert filteredOrders to CSV and trigger download
    const headers = [
    'Order ID',
    'Patient Name',
    'Service Name',
    'Amount',
    'Paid Amount',
    'Pending Amount',
    'Order Date',
    'Status',
    'Doctor Name',
    ];
    const rows = filteredOrders.map(order => [
    order.appointmentNumber,
    order.patientName,
    order.serviceName,
    order.amount,
    order.paidAmount,
    order.pendingAmount,
    formatDateForDisplay(order.orderDate),
    order.status,
    order.doctorName,
    order.invoiceNumber || ''
    ]);
    const csvContent = [
    headers.join(','),
    ...rows.map(r => r.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
    ].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    link.setAttribute('download', `ServiceReport_${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    }}
    >
    Export CSV
    </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ height: '40px' }}
                onClick={fetchOrders}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                sx={{ height: '40px' }}
                onClick={() => {
                  const now = new Date();
                  // Asia/Kolkata timezone adjustment
                  const IST_OFFSET = 5.5 * 60 * 60 * 1000;
                  const ist = new Date(now.getTime() + (now.getTimezoneOffset() * 60000) + IST_OFFSET);
                  ist.setHours(0, 0, 0, 0);
                  const istEnd = new Date(ist);
                  istEnd.setHours(23, 59, 59, 999);
                  setFromDate(ist);
                  setToDate(istEnd);
                  setStatusFilter('all');
                  setDoctorFilter('all');
                  setSelectedPatient(null);
                  setSelectedItem(null);
                  setTimeout(() => fetchOrders(), 0);
                }}
              >
                Clear Filters
              </Button>
              </Stack>
                  </Grid>
    </Grid>
    
    <TableContainer><Table><TableHead><TableRow>
                    <TableCell>Order Number</TableCell>
                    <TableCell>Order Date</TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="right">Paid Amount</TableCell>
                    <TableCell align="right">Pending Amount</TableCell>
                    <TableCell>Doctor</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.length > 0 ? (
                    filteredOrders
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((order, index) => (
                        <TableRow key={`${order.id}-${index}`}>
                          <TableCell>{order.appointmentNumber}</TableCell>
                          <TableCell>{formatDateForDisplay(order.appointment?.appointmentDate || order.orderDate)}</TableCell>
                          <TableCell>{order.patientName}</TableCell>
                          <TableCell>{order.serviceName}</TableCell>
                          <TableCell align="right">{order.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                          <TableCell align="right">{order.paidAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                          <TableCell align="right">{order.pendingAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                          <TableCell>{order.doctorName}</TableCell>
                          <TableCell>
                            <Chip 
                              label={order.status} 
                              color={getStatusColor(order.status) as any} 
                              size="small" 
                            />
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No orders found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredOrders.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
            </CardContent>
            </Card>
    </Box>
    </LocalizationProvider>
  );
}


export default ServiceReport;
